import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import images from '../../assets/images'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import { selectIsAdmin } from '../../features/Profile/profile.selectors'
import { setSelectedAdditionalDataModal } from '../../features/Reports/reports.slice'
import { selectSelectedAdditionalDataModal } from '../../features/Reports/reports.selectors'
import { createFastBoundDeltaReport } from '../../features/Reports'
import { notifyError, notifySuccess } from '../../infrastructure/notificationPopup'
import {
	CREATE_CUSTOMER_MODAL,
	DELETE_DOCUMENT_MODAL,
} from '../../features/Reports/reports.constants'
import getReportsModal from '../../features/ReportsModal'
import { ROUTES } from '../../routes.constants'
import { useAuth0 } from '@auth0/auth0-react'
import BusyIndicator, { REGIONS } from '../busyIndicator'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	NavigationMenu,
	NavigationMenuList,
	NavigationMenuItem,
	NavigationMenuLink,
} from '@/components/ui/navigation-menu'
import { DropdownMenuLabel, DropdownMenuSeparator } from '../../components/ui/dropdown-menu'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogAction,
	AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { RefreshCw } from 'lucide-react'
import { useSyncFastboundFile } from '@/features/subscriptions/api/sync-fastbound-file'

const { CUSTOMERS, PROFILE, RELEASE_NOTES, ACCOUNT, DEFAULT_ROUTE, BILLING, USERS } = ROUTES

export default function NavBar() {
	const [selectedNav, setSelectedNav] = useState('')
	const [isMobileOpen, setIsMobileOpen] = useState(false)
	const [deltaReportModalOpen, setDeltaReportModalOpen] = useState(false)
	const [deltaReportPendingFile, setDeltaReportPendingFile] = useState(null)
	const deltaReportInputRef = useRef(null)
    const syncInputRef = useRef(null)
	const { isAuthenticated } = useAuth0()
	const { logout } = useAuth0()
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const isAdmin = useSelector(selectIsAdmin)
	const selectedAdditionalDataModal = useSelector(
		selectSelectedAdditionalDataModal
	)

	useEffect(() => {
		setSelectedNav(location.pathname)
	}, [dispatch, location])

	const handleDeltaReportUpload = useCallback((event) => {
		const file = event.target.files[0]
		if (!file) return
		const allowedTypes = ['.xlsx', '.xls', '.csv']
		const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
		if (!allowedTypes.includes(ext)) {
			dispatch(notifyError('Please select an Excel file (.xlsx, .xls) or CSV file (.csv)'))
			event.target.value = ''
			return
		}
		setDeltaReportPendingFile(file)
		setDeltaReportModalOpen(true)
		event.target.value = ''
	}, [dispatch])

	const handleDeltaReportConfirm = useCallback((dryRun) => {
		if (!deltaReportPendingFile) return
		try {
			dispatch(createFastBoundDeltaReport({ file: deltaReportPendingFile, dryRun }))
		} catch (error) {
			dispatch(notifyError('Error creating delta report. Please try again later.'))
			console.error('Error creating delta report:', error)
		} finally {
			setDeltaReportPendingFile(null)
			setDeltaReportModalOpen(false)
		}
	}, [dispatch, deltaReportPendingFile])

    const syncFastboundMutation = useSyncFastboundFile({
        mutationConfig: {
            onSuccess: () => {
                dispatch(notifySuccess('Fastbound CSV uploaded for sync. Processing will begin shortly.'))
            },
            onError: () => {
                dispatch(notifyError('Failed to sync Fastbound CSV. Please try again.'))
            },
        },
    })

    const handleSyncFastboundClick = () => {
        if (syncInputRef.current) {
            syncInputRef.current.value = ''
            syncInputRef.current.click()
        }
    }

    const handleSyncFastboundChange = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        syncFastboundMutation.mutate(file)
    }

	return (
		<>
		<nav className='bg-white p-0 border-b'>
			{selectedAdditionalDataModal && getReportsModal(selectedAdditionalDataModal)}
			<div className='w-full pl-0 pr-4'>
				<div className='flex items-center justify-between'>
					<Link to={DEFAULT_ROUTE} className='bg-secondry px-4 h-14 flex items-center'>
						<img src={images.brandIcon} alt='brand-icon' height='35' width='65.63' />
					</Link>
					<button
						className='mx-3 inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-ring lg:hidden'
						aria-label='Toggle navigation'
						onClick={() => setIsMobileOpen((v) => !v)}
					>
						<svg className='h-5 w-5' viewBox='0 0 20 20' fill='currentColor'>
							<path fillRule='evenodd' d='M3 5h14M3 10h14M3 15h14' clipRule='evenodd' />
						</svg>
					</button>
					<div className='hidden lg:flex flex-1 items-center justify-between'>
						{/* Left group */}
						<div className='flex items-center ml-3'>
							<NavigationMenu viewport={false}>
								<NavigationMenuList>
									<NavigationMenuItem>
										<NavigationMenuLink asChild>
											<Link className={classNames('px-2 xl:px-3', { 'text-primary font-semibold': selectedNav === DEFAULT_ROUTE })} to={DEFAULT_ROUTE}>Dashboard</Link>
										</NavigationMenuLink>
									</NavigationMenuItem>
									<NavigationMenuItem>
										<NavigationMenuLink asChild>
											<Link className={classNames('px-2 xl:px-3', { 'text-primary font-semibold': selectedNav === CUSTOMERS })} to={CUSTOMERS}>Customers</Link>
										</NavigationMenuLink>
									</NavigationMenuItem>
									{/* <NavigationMenuItem>
											<NavigationMenuLink asChild>
												<Link className={classNames('px-2 xl:px-3', { 'text-primary font-semibold': selectedNav === USERS })} to={USERS}>Users</Link>
											</NavigationMenuLink>
										</NavigationMenuItem> */}
									<NavigationMenuItem>
										<DropdownMenu>
											<DropdownMenuTrigger className='px-2 xl:px-3 font-semibold text-primary'>Subscriptions</DropdownMenuTrigger>
											<DropdownMenuContent align='start'>
												<DropdownMenuItem asChild>
													<Link to='/plans' className='block rounded-md px-3 py-2 hover:bg-accent'>
														Plans
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link to='/subscriptions' className='block rounded-md px-3 py-2 hover:bg-accent'>
														Subscriptions
													</Link>
												</DropdownMenuItem>
												<DropdownMenuItem asChild>
													<Link to='/subscriptions/fastbound' className='block rounded-md px-3 py-2 hover:bg-accent'>
														Fastbound files
													</Link>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuLabel className='px-3 py-2 text-muted-foreground'>External</DropdownMenuLabel>
												<DropdownMenuItem asChild>
													<Link to='/subscriptions/fastbound' className='block rounded-md px-3 py-2 hover:bg-accent'>
														Fastbound subscriptions
													</Link>
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</NavigationMenuItem>
									<NavigationMenuItem>
										<DropdownMenu>
											<DropdownMenuTrigger className='px-2 xl:px-3 font-semibold text-primary'>Reports</DropdownMenuTrigger>
											<DropdownMenuContent align='start'>
												<DropdownMenuLabel className='px-3 py-2 text-muted-foreground'>Billing</DropdownMenuLabel>
												<DropdownMenuItem asChild>
													<Link to='/reports/monthly-commission' className='block rounded-md px-3 py-2 hover:bg-accent'>
														Monthly Commission
													</Link>
												</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuLabel className='px-3 py-2 text-muted-foreground'>Subscriptions</DropdownMenuLabel>
											<DropdownMenuItem asChild>
												<Link to='/reports/accounts-with-chargebee-subscriptions' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Chargebee subscriptions
												</Link>
											</DropdownMenuItem>
											<DropdownMenuItem asChild>
												<Link to='/reports/accounts-with-fastbound-subscriptions' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Fastbound subscriptions
												</Link>
											</DropdownMenuItem>
											<DropdownMenuItem asChild>
												<Link to='/reports/accounts-with-duplicate-subscriptions' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Duplicate subscriptions
												</Link>
											</DropdownMenuItem>
											<DropdownMenuItem asChild>
												<Link to='/reports/accounts-without-subscriptions' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Unsubscribed accounts
												</Link>
											</DropdownMenuItem>
											<DropdownMenuItem asChild>
												<Link to='/reports/chargebee-subscriptions-without-accounts' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Unmatched Chargebee
												</Link>
											</DropdownMenuItem>
											<DropdownMenuItem asChild>
												<Link to='/reports/fastbound-diff' className='block rounded-md px-3 py-2 hover:bg-accent'>
													Unmatched Fastbound
												</Link>
											</DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuLabel className='px-3 py-2 text-muted-foreground'>Backup Tool</DropdownMenuLabel>
                                            <DropdownMenuItem asChild>
                                                <Link to='/reports/backup-tool' className='block rounded-md px-3 py-2 hover:bg-accent'>
                                                    Backup Tool Status
                                                </Link>
                                            </DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</NavigationMenuItem>
								</NavigationMenuList>
								
							</NavigationMenu>
							<div className='px-2 xl:px-3 mr-3'>
								<Button variant='outline' className='font-semibold'
									onClick={() => {
										dispatch(setSelectedAdditionalDataModal(CREATE_CUSTOMER_MODAL))
									}}
								>
									Create Customer
								</Button>
							</div>
							<div className='px-2 xl:px-3 mr-3'>
								<Button variant='outline' className='font-semibold'
									onClick={() => {
										dispatch(setSelectedAdditionalDataModal(DELETE_DOCUMENT_MODAL))
									}}
								>
									Delete Document
								</Button>
							</div>
                            <div className='px-2 xl:px-3 mr-3'>
                                <input
                                    ref={syncInputRef}
                                    type='file'
                                    accept='.csv'
                                    className='hidden'
                                    onChange={handleSyncFastboundChange}
                                />
                                <Button variant='outline' className='font-semibold flex items-center gap-2'
                                    onClick={handleSyncFastboundClick}
                                    disabled={syncFastboundMutation.isPending}
                                >
                                    <RefreshCw className='h-4 w-4' />
                                    {syncFastboundMutation.isPending ? 'Syncing…' : 'Sync Fastbound'}
                                </Button>
                            </div>
						<div className='px-2 xl:px-3 mr-3'>
							<input
								ref={deltaReportInputRef}
								type='file'
								accept='.xlsx,.xls,.csv'
								style={{ display: 'none' }}
								onChange={handleDeltaReportUpload}
							/>
							<Button variant='outline' className='font-semibold'
								onClick={() => deltaReportInputRef.current?.click()}
							>
								<BusyIndicator region={REGIONS.CREATE_FASTBOUND_DELTA_REPORT}>
									FastBound Delta Report
								</BusyIndicator>
							</Button>
						</div>
						</div>

						{/* Right group */}
						<ul className='flex items-center'>
							{isAdmin && (
								<li className={classNames('ml-6 whitespace-nowrap', { 'text-primary font-semibold': selectedNav === RELEASE_NOTES })}>
									<Link className='px-2 xl:px-3' to={RELEASE_NOTES}>Release Notes</Link>
								</li>
							)}
							{isAdmin && (
								<li className={classNames('ml-6', { 'text-primary font-semibold': selectedNav === ACCOUNT })}>
									<Link className='px-2 xl:px-3' to={ACCOUNT}>Account</Link>
								</li>
							)}
							<li className={classNames('ml-6', { 'text-primary font-semibold': selectedNav === PROFILE })}>
								<Link className='px-2 xl:px-3' to={PROFILE}>Profile</Link>
							</li>
							{isAuthenticated && (
								<li className='ml-6 whitespace-nowrap'>
									<button
										className='px-2 xl:px-3 font-semibold'
										onClick={() => logout({ returnTo: window.location.origin })}
									>
										Log Out
									</button>
								</li>
							)}
						</ul>
					</div>
				</div>

				{/* Mobile menu */}
				{isMobileOpen && (
					<div className='lg:hidden border-t'>
						<div className='flex flex-col p-3 gap-2'>
							<Link className='px-2 py-1' to={DEFAULT_ROUTE} onClick={() => setIsMobileOpen(false)}>Dashboard</Link>
							<Link className='px-2 py-1' to={CUSTOMERS} onClick={() => setIsMobileOpen(false)}>Customers</Link>
							<Link className='px-2 py-1' to='/subscriptions/fastbound' onClick={() => setIsMobileOpen(false)}>Fastbound Files</Link>
							<div className='px-2 py-1'>
								<DropdownMenu>
									<DropdownMenuTrigger className='text-sm font-medium'>Reports</DropdownMenuTrigger>
									<DropdownMenuContent align='start'>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/monthly-commission') }}>Monthly Commission</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/accounts-with-chargebee-subscriptions') }}>Chargebee subscriptions</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/accounts-with-fastbound-subscriptions') }}>Fastbound subscriptions</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/accounts-with-duplicate-subscriptions') }}>Duplicate subscriptions</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/accounts-without-subscriptions') }}>Unsubscribed accounts</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/chargebee-subscriptions-without-accounts') }}>Unmatched Chargebee</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/fastbound-diff') }}>Unmatched Fastbound</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate('/reports/backup-tool') }}>Backup Tool Status</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
							<div className='px-2 py-1'>
								<DropdownMenu>
									<DropdownMenuTrigger className='text-sm font-medium'>Billing</DropdownMenuTrigger>
									<DropdownMenuContent align='start'>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate(BILLING) }}>Accounts</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate(BILLING + '/plans') }}>Plans</DropdownMenuItem>
										<DropdownMenuItem onSelect={(e) => { e.preventDefault(); setIsMobileOpen(false); navigate(BILLING + '/subscriptions') }}>Subscriptions</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
							<Button variant='outline' className='font-semibold'
								onClick={() => { setIsMobileOpen(false); dispatch(setSelectedAdditionalDataModal(CREATE_CUSTOMER_MODAL)) }}
							>
								Create Customer
							</Button>
							<Button variant='outline' className='font-semibold'
								onClick={() => { setIsMobileOpen(false); dispatch(setSelectedAdditionalDataModal(DELETE_DOCUMENT_MODAL)) }}
							>
								Delete Document
							</Button>
						<div>
							<Button variant='outline' className='font-semibold w-full'
								onClick={() => { setIsMobileOpen(false); deltaReportInputRef.current?.click() }}
							>
								<BusyIndicator region={REGIONS.CREATE_FASTBOUND_DELTA_REPORT}>
									FastBound Delta Report
								</BusyIndicator>
							</Button>
						</div>
							{isAdmin && (
								<Link className='px-2 py-1' to={RELEASE_NOTES} onClick={() => setIsMobileOpen(false)}>Release Notes</Link>
							)}
							{isAdmin && (
								<Link className='px-2 py-1' to={ACCOUNT} onClick={() => setIsMobileOpen(false)}>Account</Link>
							)}
							<Link className='px-2 py-1' to={PROFILE} onClick={() => setIsMobileOpen(false)}>Profile</Link>
							{isAuthenticated && (
								<button className='px-2 py-1 text-left' onClick={() => { setIsMobileOpen(false); logout({ returnTo: window.location.origin }) }}>Log Out</button>
							)}
						</div>
					</div>
				)}
			</div>
		</nav>

		<AlertDialog open={deltaReportModalOpen} onOpenChange={setDeltaReportModalOpen}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>FastBound Delta Report</AlertDialogTitle>
					<AlertDialogDescription>
						Would you like to preview the report without making any changes, or apply the changes
						(update plans, create deals, etc) to Pipedrive?
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={() => { setDeltaReportPendingFile(null); setDeltaReportModalOpen(false) }}>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						variant='outline'
						onClick={() => handleDeltaReportConfirm(true)}
					>
						Preview only
					</AlertDialogAction>
					<AlertDialogAction
						onClick={() => handleDeltaReportConfirm(false)}
					>
						Apply changes
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
		</>
	)
}
